import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import React from 'react';
import { ActionMode } from '../../hooks/useUPCActions';
import { resolveMasterLabelChromeSizing } from '../../utils/masterLabelChromeSizing';

const kbdStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 16,
  padding: '0 3px',
  height: 16,
  background: 'rgba(232,151,108,0.15)',
  border: '1px solid rgba(232,151,108,0.3)',
  borderRadius: 3,
  fontSize: 9,
  fontFamily: 'var(--font-mono)',
  fontWeight: 700,
  color: 'var(--blue)',
  lineHeight: 1,
  flexShrink: 0,
};

const actionBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-body)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

type CropActionBarLayoutVariant = 'default' | 'master-label';

export interface CropActionBarProps {
  mode: ActionMode;
  selectedCropIds: Set<string>;
  newDesignOptions?: { itemNumber: string; label: string }[];
  onDeactivate: () => void;
  onSetRep?: { designId: string; cropId: string } | null;
  onSetRepClick?: (designId: string, cropId: string) => void;
  onNewDesignClick?: (itemNumber: string) => void;
  onReviewClick?: () => void;
  undoCount: number;
  onUndo: () => void;
  idleContent?: React.ReactNode;
  onShowInfo?: () => void;
  layoutVariant?: CropActionBarLayoutVariant;
  textScale?: number;
}

function getSelectedSummaryLabel(count: number): string {
  return `${count} selected`;
}

export const CropActionBar: React.FC<CropActionBarProps> = ({
  mode,
  selectedCropIds,
  newDesignOptions = [],
  onDeactivate,
  onSetRep,
  onSetRepClick,
  onNewDesignClick,
  onReviewClick,
  undoCount,
  onUndo,
  idleContent,
  onShowInfo,
  layoutVariant = 'default',
  textScale = 100,
}) => {
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const masterLabelChrome = React.useMemo(
    () => resolveMasterLabelChromeSizing(textScale),
    [textScale]
  );
  const isMasterLabelVariant = layoutVariant === 'master-label';
  const masterLabelSharedButtonStyle: React.CSSProperties = {
    ...actionBtnStyle,
    gap: 4,
    padding: `${masterLabelChrome.actionChipPaddingY}px 4px`,
  };

  const rightControls = (
    <div
      style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
    >
      {undoCount > 0 && (
        <button
          onClick={onUndo}
          style={isMasterLabelVariant ? masterLabelSharedButtonStyle : actionBtnStyle}
          title="Undo (⌘Z)"
        >
          <UndoIcon style={{ fontSize: 12 }} />
          <span style={kbdStyle}>⌘Z</span>
        </button>
      )}
      {onShowInfo && (
        <button
          onClick={onShowInfo}
          title="Task guide (i)"
          style={{
            ...(isMasterLabelVariant ? masterLabelSharedButtonStyle : actionBtnStyle),
            padding: isMasterLabelVariant
              ? `${masterLabelChrome.actionChipPaddingY}px 6px`
              : '3px 6px',
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );

  const barStyle: React.CSSProperties = {
    flexShrink: 0,
    borderTop: '1px solid var(--border)',
    padding: '2px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };
  const defaultBarFloorStyle: React.CSSProperties = isMasterLabelVariant ? {} : { minHeight: 22 };

  const shortcutHintStyle: React.CSSProperties = {
    ...actionBtnStyle,
    cursor: 'default',
  };

  if (mode === 'crops-selected') {
    const summaryLabel = getSelectedSummaryLabel(selectedCropIds.size);
    const masterLabelSummaryTitle = `${selectedCropIds.size} selected`;
    const defaultSummaryTitle = `${selectedCropIds.size} crop${
      selectedCropIds.size > 1 ? 's' : ''
    } selected`;
    const masterLabelActionBtnStyle: React.CSSProperties = {
      ...actionBtnStyle,
      gap: 4,
      padding: `${masterLabelChrome.actionChipPaddingY}px 4px`,
    };
    const masterLabelShortcutHintStyle: React.CSSProperties = {
      ...masterLabelActionBtnStyle,
      cursor: 'default',
    };

    const renderMasterLabelAction = (
      key: string,
      shortcut: string | null,
      fullLabel: string,
      options?: {
        onClick?: () => void;
        disabled?: boolean;
      }
    ) => {
      const title = shortcut ? `${fullLabel} (${shortcut})` : fullLabel;
      const style = options?.onClick ? masterLabelActionBtnStyle : masterLabelShortcutHintStyle;

      return (
        <button
          key={key}
          onClick={options?.onClick ?? (() => {})}
          style={style}
          tabIndex={options?.onClick ? undefined : -1}
          aria-hidden={options?.onClick ? undefined : 'true'}
          title={title}
          disabled={options?.disabled}
        >
          {shortcut ? <span style={kbdStyle}>{shortcut}</span> : null}
          <span>{fullLabel}</span>
        </button>
      );
    };

    const selectedBarStyle: React.CSSProperties = isMasterLabelVariant
      ? {
          ...barStyle,
          ...defaultBarFloorStyle,
          borderTopColor: 'rgba(232,151,108,0.3)',
          background: 'rgba(232,151,108,0.06)',
          padding: `${masterLabelChrome.actionBarPaddingY}px 10px`,
          gap: 6,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
        }
      : {
          ...barStyle,
          ...defaultBarFloorStyle,
          borderTopColor: 'rgba(232,151,108,0.3)',
          background: 'rgba(232,151,108,0.06)',
        };

    if (isMasterLabelVariant) {
      return (
        <div ref={barRef} data-testid="crop-action-bar-master-label" style={selectedBarStyle}>
          <span
            title={masterLabelSummaryTitle}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--blue)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {summaryLabel}
          </span>
          {newDesignOptions.length > 0 &&
            onNewDesignClick &&
            newDesignOptions.map((option, index) =>
              renderMasterLabelAction(
                `new-design-${option.itemNumber}`,
                index === 0 ? 'D' : null,
                'New Design',
                {
                  onClick: () => onNewDesignClick(option.itemNumber),
                }
              )
            )}
          {onReviewClick
            ? renderMasterLabelAction('review', 'R', 'Review', {
                onClick: onReviewClick,
              })
            : null}
          {renderMasterLabelAction('front', 'F', 'Front')}
          {renderMasterLabelAction('cartons', 'C', 'Cartons')}
          {renderMasterLabelAction('non-front', 'N', 'Non-front')}
          {renderMasterLabelAction('inactive', 'I', 'Inactive')}
          {renderMasterLabelAction('delete', 'Del', 'Delete')}
          {onSetRep && onSetRepClick
            ? renderMasterLabelAction('set-rep', 'S', 'Set representative', {
                onClick: () => onSetRepClick(onSetRep.designId, onSetRep.cropId),
              })
            : null}
          {renderMasterLabelAction('deselect', 'Esc', 'Deselect')}
          {rightControls}
        </div>
      );
    }

    return (
      <div ref={barRef} style={selectedBarStyle}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--blue)',
          }}
        >
          {defaultSummaryTitle}
        </span>
        <div style={{ width: 1, height: 18, background: 'rgba(232,151,108,0.2)', flexShrink: 0 }} />
        <span
          style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}
        >
          Click a section or design to move
        </span>
        <div style={{ width: 1, height: 18, background: 'rgba(232,151,108,0.2)', flexShrink: 0 }} />
        {newDesignOptions.length > 0 &&
          onNewDesignClick &&
          newDesignOptions.map((option, index) => (
            <button
              key={option.itemNumber}
              onClick={() => onNewDesignClick(option.itemNumber)}
              style={actionBtnStyle}
            >
              {index === 0 && <span style={kbdStyle}>D</span>}
              {option.label}
            </button>
          ))}
        {onReviewClick && (
          <button onClick={onReviewClick} style={actionBtnStyle}>
            <span style={kbdStyle}>R</span>
            Review
          </button>
        )}
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>F</span>
          Front
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>C</span>
          Cartons
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>N</span>
          Non-front
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>I</span>
          Inactive
        </button>
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>Del</span>
          Delete
        </button>
        {onSetRep && onSetRepClick && (
          <button
            onClick={() => onSetRepClick(onSetRep.designId, onSetRep.cropId)}
            style={actionBtnStyle}
          >
            <span style={kbdStyle}>S</span>
            Set rep
          </button>
        )}
        <button onClick={() => {}} style={shortcutHintStyle} tabIndex={-1} aria-hidden="true">
          <span style={kbdStyle}>Esc</span>
          Deselect
        </button>
        {rightControls}
      </div>
    );
  }

  return (
    <div ref={barRef} style={{ ...barStyle, ...defaultBarFloorStyle }}>
      {idleContent}
      {rightControls}
    </div>
  );
};
